import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useUIStore } from '@/state/store';
import { HELP_ARTICLES, getArticle, helpCategories, searchHelp, type HelpArticle } from '@/data/helpArticles';
import { HelpFigures } from '@/components/HelpFigures';

// Minimaler Markdown-Renderer: **fett** + Absätze/Zeilenumbrüche.
function renderRich(text: string): ReactNode {
  return text.split('\n').map((line, i) => {
    if (!line.trim()) return <div key={i} style={{ height: 8 }} />;
    const parts = line.split('**');
    return (
      <p key={i} style={{ margin: '0 0 6px', lineHeight: 1.55, color: 'var(--fg-soft)' }}>
        {parts.map((p, j) => (j % 2 === 1 ? <strong key={j} style={{ color: 'var(--fg)' }}>{p}</strong> : <span key={j}>{p}</span>))}
      </p>
    );
  });
}

function ArticleView({ slug, onOpen }: { slug: string; onOpen: (slug: string) => void }) {
  const a = getArticle(slug);
  if (!a) return <div style={{ color: 'var(--muted)' }}>Artikel nicht gefunden.</div>;
  const related = a.related.map(getRelatedBySlugOrId).filter(Boolean) as HelpArticle[];
  return (
    <div>
      <div style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>{a.category}</div>
      <h2 style={{ margin: '0 0 12px', fontSize: 20, color: 'var(--fg)' }}>{a.title}</h2>
      <HelpFigures slug={a.slug} />
      <div style={{ fontSize: 13.5 }}>{renderRich(a.body)}</div>
      {related.length > 0 && (
        <div style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid var(--hairline)' }}>
          <div style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Verwandt</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {related.map((r) => (
              <button key={r.id} onClick={() => onOpen(r.slug)}
                style={{ background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', color: 'var(--fg-soft)', borderRadius: 6, padding: '5px 10px', fontFamily: 'inherit', fontSize: 12, cursor: 'pointer' }}>
                {r.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function getRelatedBySlugOrId(ref: string): HelpArticle | null {
  return getArticle(ref) ?? HELP_ARTICLES.find((a) => a.id === ref) ?? null;
}

function Row({ a, onOpen }: { a: HelpArticle; onOpen: (slug: string) => void }) {
  return (
    <div onClick={() => onOpen(a.slug)} role="button" tabIndex={0}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8, cursor: 'pointer' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
      <span style={{ color: 'var(--fg)', fontSize: 13.5 }}>{a.title}</span>
    </div>
  );
}

export function HelpPanel() {
  const open = useUIStore((s) => s.helpOpen);
  const slug = useUIStore((s) => s.helpSlug);
  const close = useUIStore((s) => s.closeHelp);
  const [sel, setSel] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => { if (open) { setSel(slug ?? null); setQuery(''); } }, [open, slug]);

  const results = useMemo(() => (query.trim() ? searchHelp(query, 20) : []), [query]);
  const cats = helpCategories();

  if (!open) return null;

  return (
    <div onClick={(e) => e.target === e.currentTarget && close()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1400, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '7vh 16px 16px' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(720px, 96vw)', maxHeight: '82vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-elev-1)', border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden' }}>
        {/* Kopf */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid var(--hairline)' }}>
          {sel ? (
            <button onClick={() => setSel(null)} title="Zurück"
              style={{ background: 'transparent', border: 'none', color: 'var(--fg-soft)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 2 }}>‹</button>
          ) : (
            <span style={{ fontSize: 16 }}>❓</span>
          )}
          <div style={{ fontWeight: 700, color: 'var(--fg)', flex: 1 }}>Hilfe</div>
          <button onClick={close} title="Schließen"
            style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 2 }}>✕</button>
        </div>

        {!sel && (
          <div style={{ padding: '10px 14px 0' }}>
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Hilfe durchsuchen…"
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', color: 'var(--fg)', borderRadius: 8, padding: '9px 11px', fontFamily: 'inherit', fontSize: 13.5, outline: 'none' }} />
          </div>
        )}

        {/* Inhalt */}
        <div style={{ overflowY: 'auto', padding: sel ? '16px 18px' : '8px 10px 14px' }}>
          {sel ? (
            <ArticleView slug={sel} onOpen={setSel} />
          ) : query.trim() ? (
            results.length === 0
              ? <div style={{ color: 'var(--muted)', padding: '10px' }}>Keine Treffer für „{query}".</div>
              : results.map((a) => <Row key={a.id} a={a} onOpen={setSel} />)
          ) : (
            cats.map((c) => (
              <div key={c} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', padding: '10px 10px 4px' }}>{c}</div>
                {HELP_ARTICLES.filter((a) => a.category === c).map((a) => <Row key={a.id} a={a} onOpen={setSel} />)}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
